import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import {
  SUBSCRIPTION_FEATURE_QUERY,
  WALLET_TOPUP_ID_QUERY,
  WALLET_TOPUP_POLL_MS,
} from 'modules/organizer/domain/constants/wallet-topup';
import { isWalletTopupTerminal } from 'modules/organizer/domain/data-mappers/wallet-topup.data-mapper';
import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import { WalletTopupPaymentMethod } from 'modules/organizer/domain/interfaces/wallet-topup';
import { redirectToExternalUrl } from 'modules/organizer/domain/utils/redirect-to-external-url';
import { parseAppFeature, pickCheapestPlanWithFeature } from 'modules/organizer/domain/utils/plan-for-feature';
import { walletTopupErrorMessage } from 'modules/organizer/domain/utils/wallet-topup-error-message';
import {
  useBuyPlanMutation,
  useCreateWalletTopupMutation,
  useGetMyWalletQuery,
  useGetTournamentPlansQuery,
  useGetWalletTopupQuery,
} from 'modules/organizer/store/billing-api';
import { useGetActiveTournamentPlanQuery } from 'modules/organizer/store/organizer-plan-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { baseApi } from 'shared/api/base-api';
import { useAppDispatch, useAppSelector } from 'store/hooks';

export interface CreateTopupArgs {
  amountInMinorUnits: number;
  paymentMethod: WalletTopupPaymentMethod;
  email: string;
}

export interface BuyPlanArgs {
  planId: number;
}

function parseTopupId(raw: string | null): number | null {
  if (raw == null || raw === '') {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

export function useSubscriptionBilling() {
  const dispatch = useAppDispatch();
  const tournamentId = useAppSelector(authStoreUserTournamentId);
  const [searchParams, setSearchParams] = useSearchParams();
  const topupIdFromUrl = parseTopupId(searchParams.get(WALLET_TOPUP_ID_QUERY));
  const [pendingTopupId, setPendingTopupId] = useState<number | null>(topupIdFromUrl);
  const [isTopupTerminal, setIsTopupTerminal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);

  const walletQuery = useGetMyWalletQuery(tournamentId as number, { skip: !tournamentId });
  const plansQuery = useGetTournamentPlansQuery(undefined, { skip: !tournamentId });
  const activePlanQuery = useGetActiveTournamentPlanQuery({ tournamentId: tournamentId as number }, { skip: !tournamentId });
  const [createTopup, createState] = useCreateWalletTopupMutation();
  const [buyPlan, buyState] = useBuyPlanMutation();
  const pendingTopupQuery = useGetWalletTopupQuery(pendingTopupId ?? 0, {
    skip: pendingTopupId == null,
    pollingInterval: isTopupTerminal ? 0 : WALLET_TOPUP_POLL_MS,
  });

  const plans = plansQuery.data ?? [];
  const activePlan = activePlanQuery.data ?? null;
  const requiredFeature = parseAppFeature(searchParams.get(SUBSCRIPTION_FEATURE_QUERY));
  const defaultPlan = useMemo(
    () => (requiredFeature && pickCheapestPlanWithFeature(plans, requiredFeature)) || pickDefaultPlan(plans, activePlan?.id),
    [plans, activePlan?.id, requiredFeature],
  );

  useEffect(() => {
    const status = pendingTopupQuery.data?.status;
    setIsTopupTerminal(!!status && isWalletTopupTerminal(status));
  }, [pendingTopupQuery.data?.status]);

  useEffect(() => {
    setIsTopupTerminal(false);
  }, [pendingTopupId]);

  useEffect(() => {
    if (pendingTopupQuery.data?.status !== 'CONFIRMED' || !tournamentId) {
      return;
    }

    dispatch(baseApi.util.invalidateTags(['Wallet', { type: 'Organizer', id: tournamentId }]));
  }, [dispatch, pendingTopupQuery.data?.status, tournamentId]);

  const startTopup = useCallback(
    async ({ amountInMinorUnits, paymentMethod, email }: CreateTopupArgs) => {
      if (!tournamentId) {
        return;
      }

      setSubmitError(null);
      try {
        const topup = await createTopup({ tournamentId, amountInMinorUnits, paymentMethod, email }).unwrap();
        if (paymentMethod === 'CARD') {
          if (!topup.paymentUrl) {
            setSubmitError('Не удалось получить ссылку на оплату. Попробуйте ещё раз.');
            return;
          }

          redirectToExternalUrl(topup.paymentUrl);
          return;
        }

        setPendingTopupId(topup.id);
      } catch (error) {
        setSubmitError(walletTopupErrorMessage(error, 'Не удалось создать платёж. Попробуйте ещё раз.'));
      }
    },
    [createTopup, tournamentId],
  );

  const payPlan = useCallback(
    async ({ planId }: BuyPlanArgs) => {
      if (!tournamentId) {
        return;
      }

      setBuyError(null);
      try {
        await buyPlan({
          tournamentId,
          planId,
          switchPlan: false,
          createRecurring: false,
        }).unwrap();
      } catch (error) {
        setBuyError(walletTopupErrorMessage(error, 'Не удалось оплатить тариф. Пополните баланс и попробуйте ещё раз.'));
      }
    },
    [buyPlan, tournamentId],
  );

  const resetTopup = useCallback(() => {
    setPendingTopupId(null);
    setSubmitError(null);
    setBuyError(null);
    setIsTopupTerminal(false);
    if (searchParams.has(WALLET_TOPUP_ID_QUERY)) {
      const next = new URLSearchParams(searchParams);
      next.delete(WALLET_TOPUP_ID_QUERY);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return {
    tournamentId,
    wallet: walletQuery.data ?? null,
    plans,
    activePlan,
    defaultPlan,
    pendingTopup: pendingTopupId == null ? null : pendingTopupQuery.data ?? null,
    isLoading: walletQuery.isLoading || plansQuery.isLoading || (!!tournamentId && activePlanQuery.isLoading),
    // Что уже известно, пока грузится остальное: скелетон по ним повторяет раскладку точно.
    hasSubscription: activePlanQuery.isSuccess ? !!activePlanQuery.data?.subscription : undefined,
    planCount: plansQuery.isSuccess ? plans.length : undefined,
    isCreating: createState.isLoading,
    isBuying: buyState.isLoading,
    submitError,
    buyError,
    startTopup,
    payPlan,
    resetTopup,
  };
}

function pickDefaultPlan(plans: TournamentPlan[], activePlanId?: number): TournamentPlan | null {
  if (activePlanId) {
    return plans.find((plan) => plan.id === activePlanId) ?? plans[0] ?? null;
  }

  return plans[0] ?? null;
}

